// Quantidade de produto vendido por quilo ou litro (pedido do dono, 29/09/2026:
// "no balcão, trabalhar da mesma forma que o cardápio digital"). Mesmo desenho
// do cardápio: linha de "− 1kg / − 500g / − 100g", o peso no meio (dá para
// digitar, com vírgula) e a linha de "+". Só para produto divisível em kg ou L.
// Mora em components/peso desde 02/10/2026: Balcão/Totem, Novo Pedido do
// Delivery, cardápio público (com e sem etapa) e o popup de Mesas usam o mesmo
// controle — antes o produto SEM etapa do cardápio e Mesas não tinham os botões.
// `variante`: "escuro" (padrão, Balcão/Totem e cardápio no tema escuro),
// "claro" (cardápio no tema claro) e "painel" (cores do tema do painel).
// `autoFoco` (30/09/2026): o popup abre com o cursor no peso (data-foco-inicial,
// lido por lib/tecladoPopupProduto) — antes abria com o foco no "Adicionar".
// Balança (26/10/2026): com `modulo` e o produto "Pesar na balança", aparece a
// faixa da balança (features/balanca/LeitorBalanca). Peso lido trava os botões
// e o campo; `setPesadoBalanca` avisa quem monta o item (pesado_balanca).
// `onEnter(peso)`: Enter no peso adiciona o produto com o peso digitado; sem
// ele, o Enter só confirma o número (como sempre foi).
import { useState } from "react";

import LeitorBalanca from "../../features/balanca/LeitorBalanca";

const PASSOS = [1, 0.5, 0.1];

export function vendePorPeso(product) {
  return !!product?.is_divisible && (product.sale_unit === "kg" || product.sale_unit === "L");
}

function rotuloPasso(valor, kg) {
  if (valor >= 1) return kg ? `${valor}kg` : `${valor}L`;
  return kg ? `${Math.round(valor * 1000)}g` : `${Math.round(valor * 1000)}ml`;
}

const CORES = {
  escuro: { menos: "bg-zinc-700 hover:bg-zinc-600 text-orange-400", visor: "bg-zinc-900", valor: "text-zinc-100", unidade: "text-zinc-400" },
  painel: { menos: "bg-orange-500/10 hover:bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-500/30", visor: "bg-card border", valor: "text-foreground", unidade: "text-muted-foreground" },
  claro: { menos: "bg-white hover:bg-orange-50 text-orange-600 border border-orange-200", visor: "bg-white", valor: "text-gray-900", unidade: "text-gray-500" },
};

export default function QuantidadePeso({ product, quantity, setQuantity, variante = "escuro", autoFoco = false, onEnter = null, modulo = null, setPesadoBalanca = null }) {
  const cor = CORES[variante] || CORES.escuro;
  const [digitando, setDigitando] = useState(null);
  const [pesado, setPesado] = useState(false);
  const marcarPesado = (v) => { setPesado(v); if (setPesadoBalanca) setPesadoBalanca(v); };
  const kg = product.sale_unit === "kg";
  // Nunca chega a zero: um "− 1kg" com 0,800 kg na tela não faz nada.
  const somar = (delta) => {
    if (pesado) return;
    const novo = parseFloat((quantity + delta).toFixed(3));
    if (novo > 0) setQuantity(novo);
  };
  // Devolve o peso que vale depois de confirmar (o digitado, se for válido).
  const confirmar = () => {
    const v = parseFloat(String(digitando ?? "").replace(",", ".").replace(/[^0-9.]/g, ""));
    const valido = !isNaN(v) && v > 0 ? parseFloat(v.toFixed(3)) : null;
    if (valido != null) setQuantity(valido);
    setDigitando(null);
    return valido ?? quantity;
  };
  const tecla = (e) => {
    if (e.key !== "Enter") return;
    if (!onEnter) { e.target.blur(); return; }
    e.preventDefault();
    onEnter(confirmar());
  };
  const botao = "h-11 rounded-lg font-bold text-sm transition-all active:scale-95";
  return (
    <div className="flex flex-col gap-2" data-e2e="quantidade-peso">
      {modulo && (
        <LeitorBalanca modulo={modulo} product={product} setQuantity={setQuantity}
          pesado={pesado} setPesado={marcarPesado} variante={variante} />
      )}
      <div className="grid grid-cols-3 gap-2">
        {PASSOS.map((p) => (
          <button key={`-${p}`} type="button" onClick={() => somar(-p)} data-e2e={`peso-menos-${p}`} disabled={pesado}
            className={`${botao} ${cor.menos} disabled:opacity-40`}>− {rotuloPasso(p, kg)}</button>
        ))}
      </div>
      <div className={`flex items-center justify-center py-2 ${cor.visor} rounded-lg gap-2`}>
        <input
          type="text"
          inputMode="decimal"
          value={digitando ?? quantity.toFixed(3).replace(".", ",")}
          readOnly={pesado}
          onFocus={(e) => { if (pesado) return; setDigitando(quantity.toFixed(3).replace(".", ",")); setTimeout(() => e.target.select(), 0); }}
          onChange={(e) => setDigitando(e.target.value)}
          onBlur={confirmar}
          onKeyDown={tecla}
          data-foco-inicial={autoFoco ? "1" : undefined}
          data-e2e="peso-valor"
          className={`w-32 text-3xl font-bold text-center bg-transparent border-0 focus:outline-none ${cor.valor}`}
        />
        <span className={`text-base font-medium ${cor.unidade}`}>{product.sale_unit}</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {PASSOS.map((p) => (
          <button key={`+${p}`} type="button" onClick={() => somar(p)} data-e2e={`peso-mais-${p}`} disabled={pesado}
            className={`${botao} disabled:opacity-40 bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-500/20`}>+ {rotuloPasso(p, kg)}</button>
        ))}
      </div>
    </div>
  );
}
